import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getContactosDeOrganizacionComercial, getContactosComercial, createVinculoComercial } from '../../api/client'
import { mensajeError } from './comercialLogica'
import { repartirContactos } from './organizacionesLogica'
import { organizacionesDe } from './contactosLogica'
import ComercialChip from './ComercialChip'
import ComercialBuscador from './ComercialBuscador'
import ComercialCopiable from './ComercialCopiable'

const entrada = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200'

// An organization card's Contactos tab. One row per contact, never repeated:
// the direct ones (linked to this organization) apart from those that come only
// from its subordinates, each with their links in the subtree (cargo, área,
// where) -- nothing is copied up the hierarchy. Fixed criteria, no switches:
// subordinates included, active links only, archived contacts left out. So the
// counts are of the contacts shown: each one once, direct if any of its links is.
// With comercial_editar: a new contact already linked here
// (?contacto=nueva&desde=<id>), or an existing one linked here with a cargo
// (never one already linked: one link per organization).
export default function ComercialContactosDeOrganizacion({ org, abrir, puedeEditar, onCambio }) {
  const { t } = useTranslation('comercial')
  const orgId = org.id
  const [filas, setFilas] = useState(null)
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let vivo = true
    getContactosDeOrganizacionComercial(orgId, { subordinadas: true, inactivos: false, archivados: false })
      .then(d => { if (vivo) { setFilas(d); setError('') } })
      .catch(e => vivo && setError(mensajeError(e, t, t('contactosOrg.cargaFallo'))))
    return () => { vivo = false }
  }, [orgId, version, t])

  const { directos, deSubordinadas } = repartirContactos(filas)

  return (
    <div className="space-y-4">
      {puedeEditar && !org.archivada && (
        <Agregar orgId={orgId} abrir={abrir} onVinculado={() => { setVersion(v => v + 1); onCambio?.() }} />
      )}
      {puedeEditar && org.archivada && <p className="text-xs text-gray-400">{t('contactosOrg.archivadaNota')}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!filas && !error && <p className="text-sm text-gray-400">{t('panel.cargando')}</p>}
      {filas && (
        <>
          {/* The total alone ("0 contactos" too); split only when some come from subordinates. */}
          <p className="text-xs text-gray-500">
            {t('contactosOrg.resumen', { count: filas.length })}
            {deSubordinadas.length > 0 &&
              ` · ${t('contactosOrg.resumenDirectos', { count: directos.length })} · ${t('contactosOrg.resumenSubordinadas', { count: deSubordinadas.length })}`}
          </p>
          <Grupo titulo={t('contactosOrg.directos')} filas={directos}
            vacio={t('contactosOrg.sinDirectos')} directo abrir={abrir} />
          {deSubordinadas.length > 0 && (
            <Grupo titulo={t('contactosOrg.deSubordinadas')} ayuda={t('contactosOrg.deSubordinadasAyuda')}
              filas={deSubordinadas} abrir={abrir} />
          )}
        </>
      )}
    </div>
  )
}

// "+ Nuevo contacto" (its form, already linked here) and "Vincular contacto
// existente": an active contact not yet linked here, and its cargo.
function Agregar({ orgId, abrir, onVinculado }) {
  const { t } = useTranslation('comercial')
  const [abierto, setAbierto] = useState(false)
  const [opciones, setOpciones] = useState(null)
  const [elegido, setElegido] = useState(null)
  const [cargo, setCargo] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!abierto) return undefined
    let vivo = true
    // Those already linked here (inactive links too) are left out.
    Promise.all([
      getContactosComercial({ archivados: 'no' }),
      getContactosDeOrganizacionComercial(orgId, { subordinadas: false, inactivos: true, archivados: true }),
    ]).then(([todos, aqui]) => {
      if (!vivo) return
      const ya = new Set(aqui.map(f => f.contacto.id))
      setOpciones(todos.filter(c => !ya.has(c.id)).map(c => ({
        value: c.id, label: c.nombre_completo,
        detalle: [...new Set(organizacionesDe(c).filter(Boolean))].join(' · ') || c.correo_principal || undefined,
      })))
    }).catch(e => vivo && setError(mensajeError(e, t, t('contactosOrg.cargaFallo'))))
    return () => { vivo = false }
  }, [abierto, orgId, t])

  const cerrar = () => { setAbierto(false); setElegido(null); setCargo(''); setError('') }
  const vincular = async () => {
    setOcupado(true)
    setError('')
    try {
      await createVinculoComercial(elegido, { organizacion_id: orgId, cargo })
      cerrar()
      onVinculado()
    } catch (err) {
      setError(mensajeError(err, t, t('catalogo.fallo')))
    } finally {
      setOcupado(false)
    }
  }

  if (!abierto) {
    return (
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => abrir('contacto', 'nueva', { desde: orgId })}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
          + {t('contactosOrg.nuevoContacto')}
        </button>
        <button type="button" onClick={() => setAbierto(true)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">
          {t('contactosOrg.vincularExistente')}
        </button>
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <p className="text-xs font-semibold text-gray-600">{t('contactosOrg.vincularExistente')}</p>
      <ComercialBuscador opciones={opciones || []} value={elegido} onChange={setElegido}
        placeholder={opciones ? t('contactosOrg.buscarContacto') : t('panel.cargando')} sinCoincidencias={t('form.sinCoincidencias')}
        aria-label={t('contactosOrg.buscarContacto')} />
      <input value={cargo} onChange={e => setCargo(e.target.value)} placeholder={t('vinculos.cargoPlaceholder')}
        aria-label={t('camposContacto.cargo')} className={entrada} />
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={cerrar} className="px-3 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-white">
          {t('catalogo.cancelar')}
        </button>
        <button type="button" onClick={vincular} disabled={ocupado || !elegido}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {t('vinculos.vincularBoton')}
        </button>
      </div>
    </div>
  )
}

function Grupo({ titulo, ayuda, filas, vacio, directo, abrir }) {
  return (
    <section>
      <div className="flex items-baseline gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${directo ? 'bg-sky-500' : 'bg-gray-300'}`} aria-hidden="true" />
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{titulo}</h3>
        <span className="text-xs text-gray-400 tabular-nums">{filas.length}</span>
      </div>
      {ayuda && <p className="text-xs text-gray-400 mb-2">{ayuda}</p>}
      {filas.length
        ? <ul className={`space-y-2 border-l-2 pl-3 ${directo ? 'border-sky-200' : 'border-gray-200'}`}>
            {filas.map(f => <Contacto key={f.contacto.id} fila={f} abrir={abrir} />)}
          </ul>
        : <p className="text-xs text-gray-400 pl-4">{vacio}</p>}
    </section>
  )
}

function Contacto({ fila, abrir }) {
  const { t } = useTranslation('comercial')
  const c = fila.contacto
  return (
    <li className="rounded-lg border border-gray-100 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => abrir('contacto', c.id)}
          className={`text-sm font-medium hover:underline ${c.archivado ? 'text-gray-400' : 'text-gray-900'}`}>
          {c.nombre_completo}
        </button>
        {c.etiquetas.map(e => <ComercialChip key={e.id} variante="etiqueta">{e.nombre}</ComercialChip>)}
        {c.archivado && <ComercialChip variante="archivado">{t('chips.archivado')}</ComercialChip>}
      </div>
      {(c.correo_principal || c.telefono_principal) && (
        <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
          {c.correo_principal && <ComercialCopiable valor={c.correo_principal} className="text-gray-500 hover:text-gray-900" />}
          {c.telefono_principal && (
            <ComercialCopiable valor={c.telefono_principal} tipo="telefono" className="text-gray-500 hover:text-gray-900" />
          )}
        </p>
      )}
      <ul className="mt-1.5 space-y-1">
        {fila.vinculos.map(v => (
          <li key={v.id} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-600">
            <span className={v.activo ? '' : 'text-gray-400 line-through'}>
              {[v.cargo, v.area].filter(Boolean).join(' · ') || t('contactosOrg.sinCargo')}
            </span>
            {v.heredado && (
              <>
                <span className="text-gray-300" aria-hidden="true">↳</span>
                <button type="button" onClick={() => abrir('org', v.organizacion_id)}
                  className={`hover:underline ${v.organizacion.archivada ? 'text-gray-400' : 'text-gray-500 hover:text-gray-900'}`}>
                  {v.ruta}
                </button>
                <ComercialChip variante="heredado">{t('chips.heredado')}</ComercialChip>
              </>
            )}
            {!v.activo && <ComercialChip variante="inactivo">{t('chips.inactivo')}</ComercialChip>}
          </li>
        ))}
      </ul>
    </li>
  )
}
